import { 
  registerWordPressSite, 
  initiateWordPressPluginBackup,
  getPluginBackupStatus,
  type WordPressRegistration,
  type BackupType
} from "@/utils/wordpressApiExtended";
import { type BackupResult, type WordPressCredentials } from "@/utils/wordpressApi";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "@/hooks/use-toast";
import { saveOrUpdateBackupInSupabase } from "./supabaseBackupService";

// Import browser-compatible database services
import { 
  initDatabase,
  getCredentials, 
  saveCredentials 
} from './databaseService';

// Map to store website plugin registrations (this will be migrated to database)
const websitePluginRegistrations: Record<string, WordPressRegistration> = {};

/**
 * Saves WordPress plugin registration for a website
 */
export const savePluginRegistration = (
  websiteId: string,
  registration: WordPressRegistration
): void => {
  websitePluginRegistrations[websiteId] = registration;
  console.log(`Plugin registration saved for website ${websiteId}`, registration);
}

/**
 * Gets WordPress plugin registration for a website
 */
export const getPluginRegistration = (websiteId: string): WordPressRegistration | undefined => {
  return websitePluginRegistrations[websiteId];
}

/**
 * Generates a unique API key for the WordPress plugin
 */
export const generateApiKey = (): string => {
  return `bsh_${uuidv4().replace(/-/g, '')}`;
}

/**
 * Saves WordPress credentials for a website
 */
export const saveWordPressCredentials = async (
  websiteId: string,
  credentials: WordPressCredentials
): Promise<void> => {
  await saveCredentials(websiteId, credentials);
  console.log(`WordPress credentials saved for website ${websiteId}`);
}

/**
 * Gets WordPress credentials for a website
 */
export const getWordPressCredentials = async (websiteId: string): Promise<WordPressCredentials | null> => {
  return await getCredentials(websiteId);
}

/**
 * Tests connection to WordPress plugin - REAL IMPLEMENTATION
 */
export const testWordPressPluginConnection = async (
  registration: WordPressRegistration
): Promise<boolean> => {
  try {
    const statusUrl = `${registration.siteUrl}/wp-json/websitebackup/v1/status`;
    
    const response = await fetch(statusUrl, {
      headers: {
        'Authorization': `Bearer ${registration.apiKey}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('WordPress plugin status:', data);
      return data.status === 'connected';
    }
    
    return false;
  } catch (error) {
    console.error('Error testing WordPress plugin connection:', error);
    return false;
  }
}

/**
 * Connects a WordPress site via the plugin - REAL IMPLEMENTATION
 */
export const connectWordPressSiteViaPlugin = async (
  websiteId: string,
  siteUrl: string,
  apiKey: string,
  credentials: WordPressCredentials
): Promise<boolean> => {
  try {
    // Create a new registration
    const registration: WordPressRegistration = {
      siteId: websiteId,
      apiKey: apiKey,
      siteUrl: siteUrl
    };
    
    // Test the connection first
    const isConnected = await testWordPressPluginConnection(registration);
    
    if (isConnected) {
      // Save the registration and credentials
      savePluginRegistration(websiteId, registration);
      await saveWordPressCredentials(websiteId, credentials);
      
      toast({
        title: "WordPress site connected",
        description: "Successfully connected to the WordPress site via plugin",
      });
      
      return true;
    }
    
    toast({
      title: "Connection failed",
      description: "Failed to connect to the WordPress site. Please check your settings and ensure the plugin is installed.",
      variant: "destructive",
    });
    
    return false;
  } catch (error) {
    console.error('Error connecting WordPress site via plugin:', error);
    
    toast({
      title: "Connection failed",
      description: "There was an error connecting to the WordPress site. Please check the URL and API key.",
      variant: "destructive",
    });
    
    return false;
  }
}

/**
 * Initiates a backup for a WordPress website using the plugin with enhanced progress tracking
 */
export const backupWebsiteViaPlugin = async (
  websiteId: string, 
  backupType: BackupType = 'incremental'
): Promise<BackupResult | null> => {
  const registration = getPluginRegistration(websiteId);
  
  if (!registration) {
    console.error("No plugin registration found for website", websiteId);
    
    toast({
      title: "Backup failed",
      description: "This website is not connected via the WordPress plugin. Please connect it first.",
      variant: "destructive",
    });
    
    return null;
  }
  
  try {
    const backupId = `bsh_${backupType}_${Date.now()}`;
    const apiUrl = `${registration.siteUrl}/wp-json/websitebackup/v1/backup/generate`;
    
    console.log(`Initiating ${backupType} backup via plugin for website ${websiteId}`);
    console.log(`API URL: ${apiUrl}`);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${registration.apiKey}`
      },
      body: JSON.stringify({
        backup_id: backupId,
        backup_type: backupType,
        website_id: websiteId
      })
    });
    
    if (!response.ok) {
      throw new Error(`WordPress API call failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('WordPress plugin backup response:', data);
    
    if (data.status === 'success') {
      const result: BackupResult = {
        id: data.backup_id || backupId,
        createdAt: new Date().toISOString(),
        status: 'in-progress',
        size: 'Initializing...',
        progress: 0,
        backupType: backupType
      };
      
      // IMMEDIATELY save the initial backup to Supabase
      console.log(`💾 Saving initial backup to Supabase: ${backupId}`);
      const saveSuccess = await saveOrUpdateBackupInSupabase(websiteId, result);
      
      if (saveSuccess) {
        console.log(`✅ Initial backup saved to Supabase successfully: ${backupId}`);
      } else {
        console.error(`❌ Failed to save initial backup to Supabase: ${backupId}`);
      }
      
      toast({
        title: `${backupType === 'incremental' ? 'Incremental' : 'Full'} backup initiated`,
        description: `Your ${backupType} backup is now in progress via the WordPress plugin.`,
      });
      
      // Start monitoring with enhanced progress tracking
      setTimeout(() => {
        monitorBackupProgressWithRealTimeUpdates(websiteId, backupId, registration);
      }, 1000);
      
      return result;
    } else {
      throw new Error(data.message || 'Backup initiation failed');
    }
  } catch (error) {
    console.error(`Error initiating ${backupType} backup via plugin for website ${websiteId}:`, error);
    
    toast({
      title: "Backup failed",
      description: `There was an error starting your ${backupType} backup via the WordPress plugin. Please try again.`,
      variant: "destructive",
    });
    
    return null;
  }
}

/**
 * Enhanced monitoring with real-time progress updates
 */
const monitorBackupProgressWithRealTimeUpdates = async (
  websiteId: string,
  backupId: string,
  registration: WordPressRegistration
): Promise<void> => {
  console.log(`🔍 Starting enhanced backup monitoring for ${backupId}`);
  let consecutiveErrors = 0;
  const maxErrors = 3;
  
  const checkStatus = async (): Promise<void> => {
    try {
      const statusUrl = `${registration.siteUrl}/wp-json/websitebackup/v1/backup/${backupId}`;
      
      console.log(`📡 Checking backup status at: ${statusUrl}`);
      
      const response = await fetch(statusUrl, {
        headers: {
          'Authorization': `Bearer ${registration.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log(`📊 Enhanced backup ${backupId} status:`, data);
        
        // Reset error counter on successful response
        consecutiveErrors = 0;
        
        const updatedBackup: BackupResult = {
          id: backupId,
          createdAt: data.created_at || new Date().toISOString(),
          status: data.status,
          size: data.size || 'Calculating...',
          progress: data.progress || 0,
          downloadUrl: data.download_url,
          backupType: data.type || 'full'
        };
        
        // Update in Supabase with real progress
        const updateSuccess = await saveOrUpdateBackupInSupabase(websiteId, updatedBackup);
        
        if (updateSuccess) {
          console.log(`✅ Backup ${backupId} updated in Supabase with progress: ${data.progress}%`);
        } else {
          console.error(`❌ Failed to update backup ${backupId} in Supabase`);
        }
        
        if (data.status === 'completed') {
          console.log(`✅ Backup ${backupId} completed successfully with size: ${data.size}`);
          toast({
            title: "Backup completed",
            description: `Your backup has been successfully completed. Final size: ${data.size}`,
          });
          return; // Stop monitoring
        } else if (data.status === 'failed') {
          console.log(`❌ Backup ${backupId} failed`);
          toast({
            title: "Backup failed",
            description: "Your backup failed. Please check the WordPress plugin logs.",
            variant: "destructive",
          });
          return; // Stop monitoring
        }
        
        // Continue monitoring with faster intervals for active backups
        const nextCheckInterval = data.progress > 0 ? 3000 : 5000; // 3s if active, 5s if starting
        console.log(`🔄 Backup ${backupId} at ${data.progress}% - will check again in ${nextCheckInterval/1000}s`);
        setTimeout(checkStatus, nextCheckInterval);
      } else {
        consecutiveErrors++;
        console.error(`❌ Failed to check backup status: ${response.status} ${response.statusText} (Error ${consecutiveErrors}/${maxErrors})`);
        
        if (consecutiveErrors >= maxErrors) {
          console.error(`❌ Too many consecutive errors checking backup ${backupId}, stopping monitoring`);
          toast({
            title: "Monitoring stopped",
            description: "Unable to check backup progress. Please refresh the page to see the latest status.",
            variant: "destructive",
          });
          return;
        }
        
        // Retry with longer delay on error
        setTimeout(checkStatus, 10000);
      }
    } catch (error) {
      consecutiveErrors++;
      console.error(`Error checking backup progress (${consecutiveErrors}/${maxErrors}):`, error);
      
      if (consecutiveErrors >= maxErrors) {
        console.error(`❌ Too many consecutive errors, stopping monitoring for backup ${backupId}`);
        return;
      }
      
      // Continue monitoring even if there's an error, but with longer delay
      setTimeout(checkStatus, 10000);
    }
  };
  
  // Start monitoring
  setTimeout(checkStatus, 2000);
};

/**
 * Checks the status of a backup initiated via the plugin with enhanced data
 */
export const checkPluginBackupStatus = async (
  websiteId: string, 
  backupId: string
): Promise<BackupResult | null> => {
  const registration = getPluginRegistration(websiteId);
  
  if (!registration) {
    console.error("No plugin registration found for website", websiteId);
    return null;
  }
  
  try {
    const statusUrl = `${registration.siteUrl}/wp-json/websitebackup/v1/backup/${backupId}`;
    
    console.log(`📡 Checking backup status: ${statusUrl}`);
    
    const response = await fetch(statusUrl, {
      headers: {
        'Authorization': `Bearer ${registration.apiKey}`
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`📊 Backup status response:`, data);
      
      return {
        id: data.id || backupId,
        createdAt: data.created_at || new Date().toISOString(),
        status: data.status,
        size: data.size || 'Calculating...',
        progress: data.progress || 0,
        downloadUrl: data.download_url,
        backupType: data.type || 'full'
      };
    } else {
      console.error(`❌ Failed to check backup status: ${response.status} ${response.statusText}`);
    }
    
    return null;
  } catch (error) {
    console.error('Error checking plugin backup status:', error);
    return null;
  }
}

/**
 * Setup demo plugin registration for testing
 */
export const setupDemoPluginRegistration = async (): Promise<void> => {
  // Initialize database if not already initialized
  await initDatabase();
  
  // Add a plugin registration for Drive Expert site
  savePluginRegistration("4", {
    siteId: "4",
    apiKey: "demo_api_key_for_testing",
    siteUrl: "https://www.drive-expert.com"
  });
  
  // Also save demo WordPress credentials for the same site
  await saveWordPressCredentials("4", {
    siteUrl: "https://www.drive-expert.com",
    username: "driveexp",
    password: "##Test$$Blog##"
  });
  
  // Continue with other demo registrations
  savePluginRegistration("1", {
    siteId: "1",
    apiKey: "demo_api_key_12345",
    siteUrl: "https://myblog.example.com"
  });
  
  // Also save demo WordPress credentials for the same site
  await saveWordPressCredentials("1", {
    siteUrl: "https://myblog.example.com",
    username: "admin",
    password: "demo_password"
  });
}
